import { AppError } from "@micd-lti/shared";
import { createApi, micd } from "mathicando-api";
import { getApiKey, getMicdData, postDeepLink } from "./api/api";
import { showError } from "./error/error";
import { removeWaitSpinnerImmediate, showWaitSpinner } from "./spinner/spinner";
import { mathmlToDocx } from "./export/omml";
import { mathmlToLatex } from "./export/latex";
import { exportAsPython, exportAsJupyter } from "./export/python";

interface UIElements {
  menuContainer: HTMLElement;
  helpButton: HTMLButtonElement;
  cancelButton: HTMLButtonElement;
  submitButton: HTMLButtonElement;
  exportOmmlButton: HTMLButtonElement;
  exportPyButton: HTMLButtonElement;
  exportIpynbButton: HTMLButtonElement;
}

const getElementByIdOrThrow = <T extends HTMLElement>(
  id: string,
  typeName: string,
): T => {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Required element with id "${id}" not found`);
  }
  if (!(element instanceof (window as any)[typeName])) {
    throw new Error(`Element with id "${id}" is not of type "${typeName}"`);
  }
  return element as T;
};

const disableInteractions = (shell: micd.shell.Shell, elements: UIElements) => {
  shell.view.disabled = true;

  elements.helpButton.disabled = true;
  elements.cancelButton.disabled = true;
  elements.submitButton.disabled = true;
  elements.exportOmmlButton.disabled = true;
  elements.exportPyButton.disabled = true;
  elements.exportIpynbButton.disabled = true;
};

const enableInteractions = (shell: micd.shell.Shell, elements: UIElements) => {
  shell.view.disabled = false;

  elements.helpButton.disabled = false;
  elements.cancelButton.disabled = false;
  elements.submitButton.disabled = false;
  elements.exportOmmlButton.disabled = false;
  elements.exportPyButton.disabled = false;
  elements.exportIpynbButton.disabled = false;
};

/**
 * Parses an HTML string to find a form, appends it to the document body,
 * and then programmatically submits it. This is designed to handle the
 * deep linking response from the LTI provider by submitting the form that
 * returns the user to the learning platform.
 *
 * @param {string} formHtml - The HTML string containing the form to be submitted.
 */
const submitDeepLinkForm = (formHtml: string) => {
  // Create a temporary, invisible container to parse the form HTML string
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = formHtml;

  const formElement = tempDiv.querySelector("form");

  if (formElement) {
    // Hide the form to prevent any visual flicker
    formElement.style.display = "none";

    // Append the form to the body and explicitly submit it.
    // This is cleaner than document.write and more reliable than just adding the HTML
    // and hoping the browser runs the auto-submit script.
    document.body.appendChild(formElement);
    formElement.submit();
  } else {
    throw new Error("Insert failed.");
  }
};

const initMicd = async (
  apiKey: string,
  micdData: string,
  elements: UIElements,
) => {
  const customTheme: micd.ui.ThemeOptions = {
    sBaseTheme: "default",
    bPanelHide: true,
  };

  const micdApi = await createApi(apiKey);

  // TODO: Move this somewhere?
  document.body.style.paddingBottom = "56px";

  const shell = await micdApi.shell.create(document.body);

  micdApi.ui.changeTheme(customTheme);
  elements.menuContainer.style.visibility = "visible";

  shell.view.editor.value = micdData;
  // console.log(micdData);

  return { shell, micdApi };
};

const setupEventListeners = (
  shell: micd.shell.Shell,
  micdApi: typeof micd,
  elements: UIElements,
) => {
  elements.helpButton.addEventListener("click", () => {
    window.open("https://www.mathicando.com/help/", "_blank");
  });

  elements.exportOmmlButton.addEventListener("click", async () => {
    const editor = shell.view.editor;
    try {
      const mml = await editor.toMml(false, {
        dialect: "xml",
        declarations: "none",
        encoding: null,
        entityEscaping: "minimal",
        display: "auto",
        invisibleOperators: [],
      });
      const docxBytes = mathmlToDocx(mml);
      const blob = new Blob([docxBytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'equation.docx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      micdApi.ui.showDialog({
        title: 'Export failed. Please try again.',
        acceptButton: { type: 'button', text: 'OK', spokenLabel: 'OK', onUse: () => true },
      });
    }
  });

  const getMmlForExport = async () => {
    return shell.view.editor.toMml(false, {
      dialect: "xml",
      declarations: "none",
      encoding: null,
      entityEscaping: "minimal",
      display: "auto",
      invisibleOperators: [],
    });
  };

  elements.exportPyButton.addEventListener("click", async () => {
    try {
      const mml = await getMmlForExport();
      exportAsPython(mathmlToLatex(mml));
    } catch (e) {
      micdApi.ui.showDialog({
        title: 'Export failed. Please try again.',
        acceptButton: { type: 'button', text: 'OK', spokenLabel: 'OK', onUse: () => true },
      });
    }
  });

  elements.exportIpynbButton.addEventListener("click", async () => {
    try {
      const mml = await getMmlForExport();
      exportAsJupyter(mathmlToLatex(mml));
    } catch (e) {
      micdApi.ui.showDialog({
        title: 'Export failed. Please try again.',
        acceptButton: { type: 'button', text: 'OK', spokenLabel: 'OK', onUse: () => true },
      });
    }
  });

  let isDialogOpen = false;

  elements.cancelButton.addEventListener("click", () => {
    if (isDialogOpen) {
      return;
    }

    isDialogOpen = true;

    micdApi.ui.showDialog({
      title:
        "Are you sure you want to cancel? Any unsaved changes will be lost.",
      acceptButton: {
        type: "button",
        text: "Yes, cancel",
        spokenLabel: "Yes, cancel",
        onUse: () => {
          disableInteractions(shell, elements);
          void postDeepLink({}).then((formHtml) => {
            submitDeepLinkForm(formHtml);
          });
          isDialogOpen = false;
          return true;
        },
      },
      cancelButton: {
        type: "button",
        text: "No, keep editing",
        spokenLabel: "Keep editing",
        onUse: () => {
          isDialogOpen = false;
          return true;
        },
      },
    });
  });

  // Helper to show the generic error dialog after a failed submission.
  const handleSubmissionError = (error: unknown) => {
    enableInteractions(shell, elements);
    elements.submitButton.textContent = "Insert";
    micdApi.ui.showDialog({
      title: `There was an error converting your equation. ${
        error instanceof Error ? error.message : "An unknown error occurred."
      }`,
      acceptButton: {
        type: "button",
        text: "OK",
        spokenLabel: "Okay, close",
        onUse: () => true,
      },
    });
  };

  const attemptSubmission = async (generateWithoutSre: boolean) => {
    disableInteractions(shell, elements);
    elements.submitButton.textContent = "Inserting...";

    const editor = shell.view.editor;

    // console.log(editor.value);

    let mml: string;

    try {
      mml = await editor.toMml(false, {
        dialect: "jax",
        declarations: "none",
        encoding: null,
        entityEscaping: "minimal",
        display: "auto",
        invisibleOperators: [],
      });
    } catch (error) {
      throw new Error("MathML generation failed.");
    }

    const micdData = editor.value;

    const body: Record<string, unknown> = { mml, micdData };
    if (generateWithoutSre) {
      body.generateWithoutSre = true;
    }

    const formHtml = await postDeepLink(body);
    submitDeepLinkForm(formHtml);
  };

  elements.submitButton.addEventListener("click", async () => {
    try {
      await attemptSubmission(false);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes(__SHARED_CONFIG__.SRE_ERROR_MESSAGE)
      ) {
        micdApi.ui.showDialog({
          title:
            "Accessibility features (like screen reader text) could not be generated for this equation likely because it is invalid. Insert anyways?",
          acceptButton: {
            type: "button",
            text: "Insert",
            spokenLabel:
              "Ignore accessibility features error and insert anyway",
            onUse: () => {
              // Fire-and-forget; handler itself must be synchronous
              void (async () => {
                try {
                  await attemptSubmission(true);
                } catch (fallbackError) {
                  handleSubmissionError(fallbackError);
                }
              })();
              return true;
            },
          },
          cancelButton: {
            type: "button",
            text: "Cancel",
            spokenLabel: "Cancel insertion",
            onUse: () => {
              enableInteractions(shell, elements);
              elements.submitButton.textContent = "Insert";
              return true;
            },
          },
        });
      } else {
        // Handle all other errors.
        handleSubmissionError(error);
      }
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.altKey && event.key === "Enter") {
      event.preventDefault();
      elements.submitButton.click();
    }
    if (event.altKey && event.key === "w") {
      event.preventDefault();
      elements.cancelButton.click();
    }
  });
};

const main = async () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const statusCode = urlParams.get("statusCode");
    const message = urlParams.get("message");

    if (statusCode && message) {
      throw new AppError(Number(statusCode), message);
    }

    showWaitSpinner();

    const elements: UIElements = {
      menuContainer: getElementByIdOrThrow<HTMLElement>(
        "menu-container",
        "HTMLElement",
      ),
      helpButton: getElementByIdOrThrow<HTMLButtonElement>(
        "help-button",
        "HTMLButtonElement",
      ),
      cancelButton: getElementByIdOrThrow<HTMLButtonElement>(
        "cancel-button",
        "HTMLButtonElement",
      ),
      submitButton: getElementByIdOrThrow<HTMLButtonElement>(
        "submit-button",
        "HTMLButtonElement",
      ),
      exportOmmlButton: getElementByIdOrThrow<HTMLButtonElement>(
        "export-omml-button",
        "HTMLButtonElement",
      ),
      exportPyButton: getElementByIdOrThrow<HTMLButtonElement>(
        "export-py-button",
        "HTMLButtonElement",
      ),
      exportIpynbButton: getElementByIdOrThrow<HTMLButtonElement>(
        "export-ipynb-button",
        "HTMLButtonElement",
      ),
    };

    const { apiKey } = await getApiKey();
    const { micdData, message: micdMessage } = await getMicdData();

    const { shell, micdApi } = await initMicd(apiKey, micdData, elements);

    removeWaitSpinnerImmediate();

    if (micdMessage) {
      micdApi.ui.showDialog({
        title: micdMessage,
        acceptButton: {
          type: "button",
          text: "OK",
          onUse: () => {
            return true;
          },
        },
      });
    }

    setupEventListeners(shell, micdApi, elements);
  } catch (error) {
    removeWaitSpinnerImmediate();
    showError(error);
  }
};

window.addEventListener("load", main);

window.addEventListener("error", (event) => {
  showError(event.error);
});

window.addEventListener("unhandledrejection", (event) => {
  showError(event.reason);
});
